// Hook to fetch/put/post the /category response

/*  Shared  */
import { Notify, Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import { TFunction } from 'i18next';

/*  React  */
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useFetchCategory = (sortBy: string = 'category') => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    // calling refetchData will force a rerender on the hook
    const [shouldRefetchData, setShouldRefetch] = useState({});
    const refetchData = () => setShouldRefetch({});

    const params = useMemo(
        () =>
            sortBy !== 'category'
                ? {
                      category: '*',
                      distinct: 'category',
                      fields: '[category]',
                      size: -1,
                      sort: sortBy
                  }
                : { category: '*', size: -1, sort: 'category' },
        [sortBy]
    );

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get('/category', {
                    cancelToken: source.token,
                    params
                });

                if (
                    (sortBy === 'category' && !Response.hasData(response)) ||
                    (sortBy !== 'category' &&
                        !Response.hasData(response, { noId: true }))
                ) {
                    debug.e('Unable to load categories. No data.');
                } else if (sortBy === 'category') {
                    setCategories(Response.getData(response));
                } else {
                    setCategories(Response.getData(response, { noId: true }));
                }
            } catch (err) {
                debug.e('Unable to load categories.', err);
            }
            setLoading(false);
        };

        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [params, shouldRefetchData, sortBy]);

    return {
        categories,
        loading,
        refetchData
    };
};

interface CategoryObject {
    category: string;
    categoryId: string;
    riskId: string;
    subCategory: string;
}

export const usePutCategory = ({
    category,
    t
}: {
    category: CategoryObject;
    t: TFunction<'translation', undefined>;
}) => {
    const [saving, setSaving] = useState(false);

    const putCategory = useCallback(async () => {
        try {
            setSaving(true);
            const params = {
                category: category.category,
                riskId: category.riskId,
                subCategory: category.subCategory
            };
            await axios.put(`/category/${category.categoryId}`, params);
            Notify.s(t('GlobalAdmin.Category.saved'));
            setSaving(false);
            return true; // success = true
        } catch (err) {
            debug.e('Category could not be saved.', err);
            Notify.e(t('GlobalAdmin.Category.notSaved'));
            setSaving(false);
            return false; // success = false
        }
    }, [category, t]);

    return { putCategory, saving };
};

export const usePostCategory = ({
    category,
    t
}: {
    category: CategoryObject;
    t: TFunction<'translation', undefined>;
}) => {
    const [saving, setSaving] = useState(false);

    const postCategory = useCallback(async () => {
        try {
            setSaving(true);
            const params = {
                category: category.category,
                riskId: category.riskId,
                subCategory: category.subCategory
            };
            await axios.post(`/category`, params);
            Notify.success(t('GlobalAdmin.Category.added'));
            setSaving(false);
            return true; // success = true
        } catch (err) {
            debug.e('Category could not be added.', err);
            Notify.error(t('GlobalAdmin.Category.notAdded'));
            setSaving(false);
            return false; // success = false
        }
    }, [category, t]);

    return { postCategory, saving };
};

interface GetDefaultCategoryId {
    defaultCategoryId: string | null;
    loading: boolean;
    refetchData: () => void;
}

/**
 * Custom hook to fetch the default category id from the server
 * Default category is hardcoded to "Business Operations" with subcategory "Other"
 * @returns {GetDefaultCategoryId} Object containing:
 *   - defaultCategoryId {string|null} - ID of the default category if found
 *   - loading {boolean} - Loading state of the request
 */
export const useGetDefaultCategoryId = (): GetDefaultCategoryId => {
    const [defaultCategoryId, setDefaultCategoryId] = useState(null);
    const [loading, setLoading] = useState(true); // calling refetchData will force a rerender on the hook
    const [shouldRefetchData, setShouldRefetch] = useState({});

    const refetchData = () => setShouldRefetch({});

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get('/category', {
                    cancelToken: source.token,
                    params: {
                        category: 'Business Operations',
                        size: -1,
                        sort: 'category',
                        subCategory: 'Other'
                    }
                });

                const defaultCategory = Response.getOne(response);

                // getOne returns {} if there is no response
                if (Object.keys(defaultCategory).length === 0) {
                    debug.e('Unable to get default category. No data.');
                } else {
                    setDefaultCategoryId(defaultCategory.id);
                }
            } catch (error) {
                debug.e('Unable to get default category.', error);
            }
            setLoading(false);
        };

        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [shouldRefetchData]);

    return {
        defaultCategoryId,
        loading,
        refetchData
    };
};
